import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Table as TableComponent,
  Alert,
  Breadcrumb,
  Actions,
  Button,
  NewUserModal,
  ConfirmationModal,
  Loading
} from '../../../Components';
import { GLOBAL } from "../../../Config/global";
import * as QLIBS from '../../../qLibsHelpers';
import { APPCONFIG } from '../../../app.config';
import { DEFAULT_HEADER } from '../../../Config/userToken';
import userAuth from '../../../Config/userauth';
import { checkEmail } from '../../../Shared/Utils/form_validation';

export default function Delegado() {
  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [error, setError] = useState(false);
  const [userToDelete, setUserToDelete] = useState('');

  const baseTableColumns = useMemo(() => {
    const columns = {
      nombre: { title: "Nombre", field: "nombre", show: true, width: 250 },
      apellido: { title: "Apellido", field: "apellido", show: true, width: 250 },
      cuit: { title: "CUIT", field: "cuit", show: true },
      email: { title: "Email", field: "email", show: true },
      estado: { title: "Estado", field: "estado", show: true }
    };
    return QLIBS.getTableColumns(columns);
  }, []);

  const detailsColumn = useMemo(() => ({
    Header: "",
    accessor: "actions",
    width: 100,
    style: { display: 'flex', alignItems: 'center' },
    filterable: false,
    sortable: false,
    resizable: true,
    Cell: ({ row }) => (
      <Actions
        customTitle={'Eliminar usuario'}
        customIcon={'fa fa-trash'}
        onCustom={() => {
          setShowDeleteModal(true);
          setUserToDelete(row.cuit);
        }}
      />
    ),
  }), []);

  const listTableColumns = useMemo(() => [detailsColumn, ...baseTableColumns.filter(c => c.show)], [detailsColumn, baseTableColumns]);

  const getData = useCallback(() => {
    setLoadingData(true);
    const { PRODUCERURL } = APPCONFIG.ENDPOINTS;

    fetch(`${PRODUCERURL}api/Producer/Delegados/?userId=${userAuth.userId()}`, {
      method: 'GET',
      headers: DEFAULT_HEADER.get()
    })
      .then(QLIBS.authorizeAction)
      .then(response => {
        if (Array.isArray(response)) {
          const data = response.map(item => ({ ...item }));
          setTableData(data);
        }
      })
      .finally(() => setLoadingData(false));
  }, []);

  const removeUser = useCallback((cuit) => {
    setLoadingData(true);
    const { PRODUCERURL } = APPCONFIG.ENDPOINTS;

    fetch(`${PRODUCERURL}api/Producer?cuit=${cuit}`, {
      method: 'DELETE',
      headers: DEFAULT_HEADER.get()
    })
      .then(QLIBS.authorizeAction)
      .then(() => getData())
      .finally(() => {
        setLoadingData(false);
        setShowDeleteModal(false);
      });
  }, [getData]);

  const createNewUser = () => setShowModal(true);
  const hideModal = () => setShowModal(false);

  const saveUser = async (input) => {
    if (!input || !input.cuit || !input.nombre || !input.apellido) {
      setAlertMessage('Por favor, complete todos los campos.');
      setError(true);
      setShowModal(true);
      hideModal();
      return;
    }

    if (!checkEmail(input.email)) {
      setAlertMessage('El email ingresado no es válido.');
      setError(true);
      setShowModal(true);
      hideModal();
      return;
    }

    const { PRODUCERURL } = APPCONFIG.ENDPOINTS;

    const requestBody = {
      Application: GLOBAL.APPNAME,
      Name: input.nombre,
      LastName: input.apellido,
      CUIT: input.cuit?.replace(/-/g, ""),
      Email: input.email,
      Telephone: input.telephone,
      TelephoneCompany: input.compania,
      reportTo: userAuth.userId(),
      Subusuario: true,
      ReportToName: userAuth.userName(),
      Rol: 'Gestor'
    };

    try {
      setLoadingData(true);
      const response = await fetch(`${PRODUCERURL}api/login/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      const json = await response.json();
      if (json.success) {
        getData();
        setAlertMessage('Subusuario creado de forma correcta.');
        setError(false);
      } else {
        setAlertMessage(json.businessExceptions?.[0]?.message || 'Error desconocido.');
        setError(true);
      }
    } catch (err) {
      setAlertMessage('Error al contactar al servidor');
      setError(true);
    } finally {
      setLoadingData(false);
      hideModal();
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <Loading visible={loadingData} />
      <section className="col-12">
        <div className="row">
          <div className="col-12"><Breadcrumb currentPage="/delegado" /></div>
          <div className="col-12">
            <h4 className="form-title">{GLOBAL.PAGES.TITLE.DELEGADO}</h4>
          </div>
          <div className="form-container container table-view col-md-12 mt-2">

            {showDeleteModal && (
              <ConfirmationModal
                message={GLOBAL.TABLE_VIEW.DELETE_MODAL_SUBUSER}
                status="primary"
                title={'Eliminar subusuario'}
                onDismiss={() => setShowDeleteModal(false)}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={() => removeUser(userToDelete)}
              />
            )}

            {showModal && (
              <NewUserModal
                status="primary"
                title="Agregar nuevo usuario"
                producer='true'
                onDismiss={hideModal}
                onConfirm={saveUser}
                user={{}} // puedes inicializar con valores por defecto si es necesario
              />
            )}

            {alertMessage && (
              <div className="row pt-4">
                <div className="col-12 px-0">
                  <Alert
                    icon="info"
                    noDetail={true}
                    type={error ? 'error' : 'success'}
                    title={error ? 'Error: ' : '¡Hecho! '}
                    message={alertMessage}
                  />
                </div>
              </div>
            )}

            <div className="row col-12 my-3">
              <div className="search-buttons">
                <Button
                  type="button"
                  id="btnNuevo"
                  onClick={createNewUser}
                  classes="to-bottom button-small search-button"
                >
                  <span className="fa fa-plus"> </span> Nuevo usuario
                </Button>
              </div>
            </div>

            <div className="row my-3">
              <div className="col-12 px-3">
                <div className="col-12 mb-2">
                  <TableComponent columns={listTableColumns} data={tableData} />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
